import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { 
  Plus, 
  Search, 
  Users, 
  Calendar,
  FileAudio,
  Share, 
  Video,
  Music
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import ProjectCard from '@/components/collaboration/ProjectCard';
import SessionCard from '@/components/collaboration/SessionCard';
import FileCard from '@/components/collaboration/FileCard';
import { 
  mockCollaborationProjects, 
  mockCollaborationSessions,
  mockProjectFiles,
  CollaborationProject
} from '@/types/collaboration';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';

const Collaboration = () => {
  const [activeTab, setActiveTab] = useState('projects');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState<Partial<CollaborationProject>>({
    name: '',
    description: '',
    type: 'recording'
  });
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const handleCreateProject = () => {
    toast({
      title: "Project Created",
      description: "Your collaboration project has been created successfully."
    });
    setCreateDialogOpen(false);
    setNewProject({
      name: '',
      description: '',
      type: 'recording'
    });
  };
  
  const handleJoinSession = (sessionId: string) => {
    toast({
      title: "Joining Session",
      description: "Preparing virtual studio environment..."
    });
    // In a real application, this would navigate to the session room or launch a video call
  };
  
  const handlePlayFile = (file: any) => {
    toast({
      title: "Playing File",
      description: `Now playing: ${file.name}`
    });
    // In a real application, this would play the file
  };
  
  return (
    <Layout>
      <div className="container py-6 max-w-7xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Collaboration Studio</h1>
            <p className="text-muted-foreground mt-1">
              Create and collaborate on music projects with other artists
            </p>
          </div>
          <Button 
            onClick={() => setCreateDialogOpen(true)}
            className="flex items-center gap-1"
          >
            <Plus className="h-4 w-4" />
            <span>New Project</span>
          </Button>
        </div>
        
        <Tabs 
          defaultValue="projects" 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="space-y-6"
        >
          <TabsList className="grid grid-cols-3 max-w-md">
            <TabsTrigger value="projects" className="flex items-center gap-1">
              <Music className="h-4 w-4" />
              <span>Projects</span>
            </TabsTrigger>
            <TabsTrigger value="sessions" className="flex items-center gap-1">
              <Video className="h-4 w-4" />
              <span>Sessions</span>
            </TabsTrigger>
            <TabsTrigger value="files" className="flex items-center gap-1">
              <FileAudio className="h-4 w-4" />
              <span>Files</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="projects" className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
              <div className="relative w-full md:w-72">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search projects..."
                  className="pl-8"
                />
              </div>
              <div className="flex items-center gap-2">
                <Select defaultValue="all">
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
                <Select defaultValue="all">
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Project type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="writing">Writing</SelectItem>
                    <SelectItem value="recording">Recording</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                    <SelectItem value="mixing">Mixing</SelectItem>
                    <SelectItem value="mastering">Mastering</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mockCollaborationProjects.map(project => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="sessions" className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
              <div className="relative w-full md:w-72">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search sessions..."
                  className="pl-8"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" className="flex gap-1 items-center">
                  <Calendar className="h-4 w-4" />
                  <span>Schedule Session</span>
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-dashed">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-muted-foreground flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Upcoming Sessions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {mockCollaborationSessions
                    .filter(s => s.status === 'scheduled' || s.status === 'Scheduled')
                    .map(session => (
                      <SessionCard 
                        key={session.id} 
                        session={session} 
                        onJoin={handleJoinSession}
                      />
                    ))}
                </CardContent>
              </Card>
              
              <Card className="border-dashed">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-muted-foreground flex items-center gap-2">
                    <Video className="h-4 w-4" />
                    Live Now
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {mockCollaborationSessions
                    .filter(s => s.status === 'live' || s.status === 'Live')
                    .length > 0 ? (
                    mockCollaborationSessions
                      .filter(s => s.status === 'live' || s.status === 'Live')
                      .map(session => (
                        <SessionCard 
                          key={session.id} 
                          session={session} 
                          onJoin={handleJoinSession}
                        />
                      ))
                  ) : (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No live sessions at the moment
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card className="border-dashed">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-muted-foreground flex items-center gap-2">
                    <FileAudio className="h-4 w-4" />
                    Past Sessions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {mockCollaborationSessions
                    .filter(s => s.status === 'completed' || s.status === 'Completed')
                    .map(session => (
                      <SessionCard 
                        key={session.id} 
                        session={session}
                      />
                    ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="files" className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
              <div className="relative w-full md:w-72">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search files..."
                  className="pl-8"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" className="flex gap-1 items-center">
                  <Share className="h-4 w-4" />
                  <span>Share Files</span>
                </Button>
                <Button variant="default" className="flex gap-1 items-center">
                  <Plus className="h-4 w-4" />
                  <span>Upload</span>
                </Button>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-base font-semibold">Recently Uploaded</h3>
              <div className="space-y-3">
                {mockProjectFiles
                  .sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime())
                  .slice(0, 5)
                  .map(file => (
                    <FileCard 
                      key={file.id} 
                      file={file}
                      onPlay={handlePlayFile}
                    />
                  ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Create Project Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Create New Collaboration Project</DialogTitle>
              <DialogDescription>
                Set up a new project to collaborate with other musicians
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Project Name</Label>
                <Input 
                  id="name" 
                  value={newProject.name} 
                  onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                  placeholder="e.g. Summer EP Collaboration"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="type">Project Type</Label>
                <Select 
                  value={newProject.type} 
                  onValueChange={(value) => setNewProject({
                    ...newProject, 
                    type: value as CollaborationProject['type']
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="writing">Songwriting</SelectItem>
                    <SelectItem value="recording">Recording</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                    <SelectItem value="mixing">Mixing</SelectItem>
                    <SelectItem value="mastering">Mastering</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description" 
                  value={newProject.description} 
                  onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                  placeholder="Briefly describe what this project is about"
                  rows={3}
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="collaborators">Invite Collaborators (Optional)</Label>
                <div className="flex gap-2">
                  <Input 
                    id="collaborators" 
                    placeholder="Enter email or username"
                  />
                  <Button variant="outline" className="shrink-0">
                    <Users className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  You can add more collaborators after creating the project
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateProject}>
                Create Project
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Collaboration;
